import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';
import WaitlistCTA, { WaitlistCTAGroup } from '../components/WaitlistCTA';
import { getSortedArticles } from '../data/articles';

export default function Blog() {
  const navigate = useNavigate();
  const articles = getSortedArticles();

  const goToWaitlist = (role) => {
    navigate('/?scroll=waitlist&role=' + role);
  };

  return (
    <div className="min-h-screen" data-testid="blog-page">
      <PublicNav currentPage="blog" />

      {/* Hero Section - Light */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4">From the Blog</h1>
          <p className="text-lg text-[#334d6e] max-w-2xl mx-auto">
            Insights on founder-investor alignment, early-stage partnerships, and building meaningful connections.
          </p>
        </div>
      </section>

      {/* Blog List - Dark */}
      <section className="bg-slate-800 py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="space-y-6" data-testid="blog-articles-list">
            {articles.map((article) => (
              <Card 
                key={article.id}
                className="bg-slate-700/50 border-slate-600 p-6 hover:border-blue-500 transition-colors group"
                data-testid={`blog-article-${article.id}`}
              >
                {/* Category */}
                <div className="text-blue-400 text-xs font-medium mb-3 uppercase">
                  {article.category}
                </div>
                
                {/* Title */}
                <h2 className="text-xl text-[#c7d4e4] font-semibold mb-3 group-hover:text-blue-400 transition-colors">
                  {article.title}
                </h2>
                
                {/* Excerpt */}
                <p className="text-[#a3b8d1] text-sm mb-4 leading-relaxed">
                  {article.excerpt}
                </p>
                
                {/* Meta Row */}
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-4 text-xs text-[#a3b8d1]">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {article.readTime}
                    </span>
                  </div>
                  
                  {/* Read Full Article Link */}
                  <Link 
                    to={`/blog/${article.id}`}
                    className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                    data-testid={`read-article-${article.id}`}
                  >
                    Read full article
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Light */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">Stay Updated</h2>
          <p className="text-[#334d6e] mb-8">
            Join the waitlist to receive new articles and platform updates.
          </p>
          <WaitlistCTAGroup 
            onFounderClick={() => goToWaitlist('founder')}
            onInvestorClick={() => goToWaitlist('investor')}
            theme="light"
            size="default"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
